import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    FileTextOutlined,
    MoreOutlined,
    SendOutlined,
    ShareAltOutlined
} from '@ant-design/icons'
import type { AdCampaign } from '@entities/ad-campaign'
import { AdCampaignType } from '@entities/ad-campaign/api/types'
import { useAdCampaigns } from '@entities/ad-campaign/model/hooks/use-ad-campaigns'
import { useChannelStore } from '@entities/channel'
import { AddAdsPostModal, EditAdsPostModal, PreviewPostModal, usePosts, type Post } from '@entities/post'
import { getPosts } from '@entities/post/api'
import type { AdLink } from '@entities/post/model/types'
import { AdPriceMethod } from '@entities/post/model/types'
import { dateToShortString } from '@shared/lib/dateToShortString/dateToShortString'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { useQueryClient } from '@tanstack/react-query'
import type { TableColumnsType } from 'antd'
import { App, Avatar, Button, Card, Dropdown, Flex, Popconfirm, Space, Table, theme, Tooltip, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { v4 } from 'uuid'

const { Title } = Typography

interface DataType {
    key: React.Key
    name: string
    internetResources: React.ReactNode
    partners: string
    author: React.ReactNode
    preview: React.ReactNode
    views: React.ReactNode
    subscribers: React.ReactNode
    premiumAmount: React.ReactNode
    unsubs: React.ReactNode
    cpsCommmon: React.ReactNode
    cpsPure: React.ReactNode
    type: React.ReactNode
    payment: React.ReactNode
    budget: string
    comment: string
    actions: React.ReactNode
    contract: React.ReactNode
    children?: DataType[]
}

export const AdvertisingBuyPage = () => {
    const {
        token: { colorLink, colorSuccess, colorError }
    } = theme.useToken()

    const {
        actions: { previewPost, deletePost }
    } = usePosts()

    const { current: currentChannel } = useChannelStore()

    const {
        adCampaigns,
        actions: { deleteAdCampaign }
    } = useAdCampaigns({ channelId: currentChannel?.id, type: AdCampaignType.purchase })

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [editPostData, setEditPost] = useState<Post | null>(null)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)

    const onEditModalOpen = (post: Post) => {
        setEditPost(post)
        setIsEditModalOpen(true)
    }

    const onEditModalClose = () => {
        setEditPost(null)
        setIsEditModalOpen(false)
    }

    const [post, setPost] = useState<undefined | Post>()
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

    const handleSelectEvent = (post: Post) => {
        setPost(post)
        setIsPreviewModalOpen(true)
    }

    const { message } = App.useApp()

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Рекламная компания',
            dataIndex: 'name',
            key: 'name',
            align: 'center'
        },
        {
            title: 'Интернет ресурсы',
            dataIndex: 'internetResources',
            key: 'internetResources',
            align: 'center'
        },
        {
            title: 'Партнеры',
            dataIndex: 'partners',
            key: 'partners',
            align: 'center'
        },
        {
            title: 'Таймслот/автор',
            dataIndex: 'author',
            key: 'author',
            align: 'center'
        },
        {
            title: 'Пост/Креатив',
            dataIndex: 'preview',
            key: 'preview',
            align: 'center'
        },
        {
            title: 'Просмотры',
            dataIndex: 'views',
            key: 'views',
            align: 'center'
        },
        {
            title: 'ПДП',
            dataIndex: 'subscribers',
            key: 'subscribers',
            align: 'center'
        },
        {
            title: 'Отписки',
            dataIndex: 'unsubs',
            key: 'unsubs',
            align: 'center'
        },
        {
            title: 'CPS общий',
            dataIndex: 'cpsCommmon',
            key: 'cpsCommmon',
            align: 'center'
        },
        {
            title: 'CPS чистый',
            dataIndex: 'cpsPure',
            key: 'cpsPure',
            align: 'center'
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            align: 'center'
        },
        {
            title: 'Оплата',
            dataIndex: 'payment',
            key: 'payment',
            align: 'center'
        },
        {
            title: 'Цена Р',
            dataIndex: 'budget',
            key: 'budget',
            align: 'center'
        },
        // {
        //     title: 'Комментарий',
        //     dataIndex: 'comment',
        //     key: 'comment',
        //     align: 'center'
        // },
        {
            dataIndex: 'contract',
            key: 'contract',
            title: (
                <Tooltip title="Договор">
                    <FileTextOutlined style={{ fontSize: 16 }} />
                </Tooltip>
            ),
            align: 'center'
        },
        {
            dataIndex: 'actions',
            key: 'actions'
        }
    ]

    function renderAdLink(adLink: AdLink): DataType {
        return {
            key: v4(),
            name: '',
            internetResources: <Tooltip title={adLink.href}>{adLink.text}</Tooltip>,
            partners: '',
            author: '',
            preview: '',
            views: <span style={{ color: colorSuccess }}>{adLink.clickCount}</span>,
            subscribers: <span style={{ color: colorSuccess }}>-</span>,
            premiumAmount: <span style={{ color: colorSuccess }}>-</span>,
            unsubs: <span style={{ color: colorError }}>-</span>,
            cpsCommmon: <span>-</span>,
            cpsPure: <span>-</span>,
            type: '',
            payment: '',
            budget: '',
            comment: '',
            contract: null,
            actions: null
        }
    }

    function renderPostRow(record: Post): DataType {
        const isFix = record.advertisingInfo?.priceMethod === AdPriceMethod.fix
        const isPaid = record.advertisingInfo?.paymentConfirmed

        return {
            key: `post-${record.id}`,
            name: '',
            internetResources: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar src={null} size={20} />
                    {record.advertisingInfo?.internetResource.name || ''}
                </div>
            ),
            partners: record.advertisingInfo?.partner.tgUsername || '',
            author: (
                <span style={{ color: colorSuccess }}>
                    {record.publishDate ? dateToShortString(record.publishDate, { timeEnabled: true }) : ''}
                </span>
            ),
            preview: <EyeOutlined onClick={() => handleSelectEvent(record)} />,
            views: <span style={{ color: colorSuccess }}>{record.advertisingInfo?.statistics.clicks}</span>,
            subscribers: <span style={{ color: colorSuccess }}>{record.advertisingInfo?.statistics.joins}</span>,
            premiumAmount: <span style={{ color: colorSuccess }}></span>,
            unsubs: <span style={{ color: colorError }}>{record.advertisingInfo?.statistics.leaves}</span>,
            cpsCommmon:
                record.advertisingInfo?.budget && record.advertisingInfo?.statistics.joins ? (
                    <span>{record.advertisingInfo?.budget / record.advertisingInfo?.statistics.joins} Р</span>
                ) : (
                    '-'
                ),
            cpsPure:
                record.advertisingInfo?.budget && record.advertisingInfo?.statistics.joins ? (
                    <span>
                        {record.advertisingInfo?.budget /
                            (record.advertisingInfo?.statistics.joins - record.advertisingInfo?.statistics.leaves)}{' '}
                        Р
                    </span>
                ) : (
                    '-'
                ),
            type: (
                <CustomTag color={'default'} style={{ backgroundColor: 'none' }}>
                    {isFix ? 'Фикс' : 'CPM'}{' '}
                </CustomTag>
            ),
            payment: (
                <CustomTag color={isPaid ? colorSuccess : 'gold'} style={{ backgroundColor: 'none' }}>
                    {isPaid ? 'Оплачен' : 'Не оплачен'}
                </CustomTag>
            ),
            budget: record.advertisingInfo?.budget.toString() || '',
            comment: '',
            contract: (
                <Space style={{ gap: 0 }}>
                    <Tooltip title="Ссылка на Договор">
                        <Button
                            type="text"
                            style={{ padding: '4px 5px' }}
                            onClick={() =>
                                navigator.clipboard
                                    .writeText(
                                        `${window.location.origin}/api/partners/contract/${record.advertisingInfo?.contractUuid}`
                                    )
                                    .then(() => {
                                        message.success('Скопировано в буфер обмена')
                                    })
                            }
                        >
                            <ShareAltOutlined />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Отправить Договор себе в PDF">
                        <Button type="text" style={{ padding: '4px 5px' }}>
                            <SendOutlined />
                        </Button>
                    </Tooltip>
                </Space>
            ),
            actions: (
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            {
                                key: '0',
                                label: (
                                    <Space style={{ color: colorLink }}>
                                        <EyeOutlined /> Превью поста
                                    </Space>
                                ),
                                onClick: () => handleSelectEvent(record)
                            },
                            {
                                key: '1',
                                label: (
                                    <Space style={{ color: colorLink, width: '100%' }}>
                                        <EditOutlined /> Редактировать
                                    </Space>
                                ),
                                disabled:
                                    record.advertisingInfo?.customerConfirm && record.advertisingInfo.sellerConfirm,
                                onClick: () => onEditModalOpen(record)
                            },
                            {
                                key: '2',
                                label: (
                                    <Space style={{ color: colorLink, width: '100%' }}>
                                        <EyeOutlined /> Предпросмотр в бот
                                    </Space>
                                ),
                                onClick: () => previewPost(record)
                            },
                            {
                                key: '3',
                                danger: true,
                                label: (
                                    <Popconfirm
                                        title="Удалить пост"
                                        description="Вы уверены что хотите удалить пост?"
                                        onConfirm={() => deletePost(record.id)}
                                        okText="Удалить"
                                        cancelText="Отмена"
                                    >
                                        <Space style={{ width: '100%' }}>
                                            <DeleteOutlined /> Удалить
                                        </Space>
                                    </Popconfirm>
                                )
                            }
                        ]
                    }}
                >
                    <MoreOutlined />
                </Dropdown>
            ),
            children: record.advertisingInfo?.links?.map(renderAdLink)
        }
    }

    function renderAdCampaignRow(adCampaign: AdCampaign): DataType {
        return {
            key: `adcampaign-${adCampaign.id}`,
            name: adCampaign.name,
            internetResources: adCampaign.resourcesCount.toString(),
            partners: adCampaign.partnersCount.toString(),
            author: `@` + adCampaign.creator.username,
            preview: null,
            views: '',
            subscribers: '',
            premiumAmount: '',
            unsubs: '',
            cpsCommmon: '',
            cpsPure: '',
            type: null,
            payment: null,
            budget: adCampaign.spent.toString(),
            comment: '',
            contract: null,
            actions: (
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            {
                                key: '1',
                                danger: true,
                                label: (
                                    <Popconfirm
                                        title="Удалить рекламную кампанию"
                                        description="Вы уверены что хотите удалить рекламную кампанию?"
                                        onConfirm={() => deleteAdCampaign({ id: adCampaign.id })}
                                        okText="Удалить"
                                        cancelText="Отмена"
                                    >
                                        <Space style={{ width: '100%' }}>
                                            <DeleteOutlined /> Удалить
                                        </Space>
                                    </Popconfirm>
                                )
                            }
                        ]
                    }}
                >
                    <MoreOutlined />
                </Dropdown>
            ),
            children: []
        }
    }

    const [treeData, setTreeData] = useState<DataType[]>([])
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

    useEffect(() => {
        if (adCampaigns) {
            const rows = adCampaigns.map((ad) => renderAdCampaignRow(ad))
            setTreeData(rows)
        }
    }, [adCampaigns])

    const queryClient = useQueryClient()

    const onExpand = async (expanded: boolean, record: DataType) => {
        if (!expanded) {
            setExpandedRowKeys((prev) => prev.filter((key) => key !== record.key))
            return
        }

        const adCampaignId = Number((record.key as string).replace('adcampaign-', ''))

        if (adCampaignId) {
            const adsPostsQueryKey = ['ad-posts', adCampaignId]

            const { results: posts } = await queryClient.fetchQuery({
                queryKey: adsPostsQueryKey,
                queryFn: () => getPosts({ page: 1, pageSize: 1000, isAdvertising: true, adCampaignId: adCampaignId })
            })

            const postRows = posts.map(renderPostRow)

            setTreeData((prev) => {
                return prev.map((item) => {
                    if (item.key === record.key) {
                        return { ...item, children: postRows }
                    }
                    return item
                })
            })
        }

        setExpandedRowKeys((prev) => [...prev, record.key])
    }

    return (
        <Card>
            <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
                <Title level={3}>Покупка(Закуп) рекламы</Title>
                <Button type="primary" onClick={() => setIsCreateModalOpen(true)}>
                    Создать закуп
                </Button>
            </Flex>

            <Table<DataType>
                columns={columns}
                expandable={{
                    expandedRowKeys,
                    // indentSize: 20,
                    onExpand
                }}
                dataSource={treeData}
                loading={false}
                scroll={{
                    y: 'calc(100vh - 441px)'
                }}
                rowKey="key"
            />

            {isCreateModalOpen && (
                <AddAdsPostModal
                    open={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    adCampaignType={AdCampaignType.purchase}
                />
            )}

            {editPostData && (
                <EditAdsPostModal
                    editPostData={editPostData}
                    open={isEditModalOpen}
                    onClose={onEditModalClose}
                    adCampaignName={
                        adCampaigns?.find((ac) => editPostData.advertisingInfo?.adCampaign === ac.id)?.name || ''
                    }
                />
            )}

            <PreviewPostModal
                onEditModalOpen={onEditModalOpen}
                post={post}
                open={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
            />
        </Card>
    )
}
